import { faSquare as farSquare } from '@fortawesome/pro-regular-svg-icons'
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faHorizontalRule,
  faLink,
  faSquare,
  faText,
  faTimes,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Editor } from '@tiptap/core'
import { BubbleMenu } from '@tiptap/react'
import React, { useEffect, useRef } from 'react'
import { Tooltip } from '../../Tooltip/Tooltip'
import { EditorCommandButton, EditorCommandButtonProps } from './EditorCommandButton'
import { ILinkOptions, SearchLink } from './EditorLinkBubbleMenu'
import { hideAll } from 'tippy.js'

const unblur = (e: Editor | null) => {
  if (e?.isActive('button')) {
    e?.chain().blur().run()
    hideAll()
  }
}

interface IProps {
  editor: Editor | null
  linkOptions?: ILinkOptions
  disabled?: boolean
}

export const EditorButtonBubbleMenu = ({ editor, linkOptions, disabled }: IProps) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        unblur(editor)
      }
    }
    const handleClickOutside = (e: any) => {
      if (!ref.current?.contains(e.target)) {
        unblur(editor)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', down)

    handleClickOutside({ target: document.activeElement })

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', down)
    }
  }, [ref, editor])

  const { onInputChange: handleInputChange } = linkOptions || {}
  const hasButtonExtension: boolean = !!editor?.extensionManager.extensions.find(
    (e) => e.name === 'button',
  )
  if (!editor || disabled || !hasButtonExtension) return null
  const { url, text, buttonColor } = editor.getAttributes('button')
  return (
    <BubbleMenu
      className="bg-body-tertiary border rounded-xs p-2"
      tippyOptions={{
        duration: 150,
        zIndex: 1060,
        placement: 'bottom',
        moveTransition: 'transform 0.15s ease-out',
      }}
      editor={editor}
      shouldShow={({ editor: e }) => e.isActive('button')}
    >
      <div ref={ref}>
        <div className="mb-3">
          <div className="input-group">
            <span className="input-group-text border-end-0">
              <Tooltip title="Link to">
                <FontAwesomeIcon fixedWidth={true} icon={faLink} />
              </Tooltip>
            </span>
            <input
              type="text"
              name="url"
              className="form-control border-start-0 border-end-0"
              value={url}
              autoComplete="off"
              onChange={(e) => {
                editor.chain().setButtonAttributes({ url: e.target.value }).run()
                handleInputChange?.(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  e.preventDefault()
                  unblur(editor)
                }
              }}
            />
            <button
              className="btn btn-body-secondary"
              type="button"
              onClick={() => editor.chain().setButtonAttributes({ url: '' }).run()}
            >
              <FontAwesomeIcon fixedWidth={true} icon={faTimes} />
            </button>
          </div>
        </div>
        <div className="mb-3">
          <div className="input-group">
            <span className="input-group-text border-end-0">
              <Tooltip title="Text">
                <FontAwesomeIcon fixedWidth={true} icon={faAlignLeft} />
              </Tooltip>
            </span>
            <input
              type="text"
              name="text"
              className="form-control border-start-0"
              value={text}
              autoComplete="off"
              onChange={(e) => editor.chain().setButtonAttributes({ text: e.target.value }).run()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  e.preventDefault()
                  unblur(editor)
                }
              }}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="small">Style</label>
          <div className="d-flex">
            <EditorCommandButton
              className="btn text-capitalize"
              style={{
                flexBasis: 0,
                flexGrow: 1,
                maxWidth: '100%',
              }}
              isActive={() => editor.isActive('button', { variant: 'filled' })}
              onClick={() =>
                editor
                  .chain()
                  .setButtonAttributes({ variant: 'filled', textColor: '#ffffff' })
                  .run()
              }
            >
              Filled
            </EditorCommandButton>
            <EditorCommandButton
              className="btn text-capitalize"
              style={{
                flexBasis: 0,
                flexGrow: 1,
                maxWidth: '100%',
              }}
              isActive={() => editor.isActive('button', { variant: 'outline' })}
              onClick={() =>
                editor
                  .chain()
                  .setButtonAttributes({ variant: 'outline', textColor: buttonColor })
                  .run()
              }
            >
              Outline
            </EditorCommandButton>
          </div>
        </div>
        <div className="mb-3">
          <label className="small">Corner radius</label>
          <div className="d-flex">
            <EditorCommandButton
              className="btn text-capitalize"
              style={{
                flexBasis: 0,
                flexGrow: 1,
                maxWidth: '100%',
              }}
              isActive={() => editor.isActive('button', { borderRadius: 'round' })}
              onClick={() => editor.chain().setButtonAttributes({ borderRadius: 'round' }).run()}
            >
              Round
            </EditorCommandButton>
            <EditorCommandButton
              className="btn text-capitalize"
              style={{
                flexBasis: 0,
                flexGrow: 1,
                maxWidth: '100%',
              }}
              isActive={() => editor.isActive('button', { borderRadius: 'smooth' })}
              onClick={() => editor.chain().setButtonAttributes({ borderRadius: 'smooth' }).run()}
            >
              Smooth
            </EditorCommandButton>
            <EditorCommandButton
              className="btn text-capitalize"
              style={{
                flexBasis: 0,
                flexGrow: 1,
                maxWidth: '100%',
              }}
              isActive={() => editor.isActive('button', { borderRadius: 'sharp' })}
              onClick={() => editor.chain().setButtonAttributes({ borderRadius: 'sharp' }).run()}
            >
              Sharp
            </EditorCommandButton>
          </div>
        </div>
        <div className="d-flex">
          <div
            style={{
              flexBasis: 0,
              flexGrow: 1,
              maxWidth: '100%',
            }}
          >
            <label className="small">Alignment</label>
            <div className="d-flex">
              <EditorCommandButton
                title="Align left"
                className="btn btn-sm mx-2"
                isActive={() => editor.isActive('button', { alignment: 'left' })}
                onClick={() => {
                  if (editor.getAttributes('image').alignment === 'left') {
                    editor.chain().setButtonAttributes({ alignment: 'left' }).run()
                  } else {
                    editor.chain().setButtonAttributes({ alignment: 'left' }).run()
                  }
                }}
              >
                <FontAwesomeIcon icon={faAlignLeft} fixedWidth />
              </EditorCommandButton>
              <EditorCommandButton
                title="Align center"
                className="btn btn-sm mx-2"
                isActive={() => editor.isActive('button', { alignment: 'center' })}
                onClick={() => {
                  if (editor.getAttributes('image').alignment === 'center') {
                    editor.chain().setButtonAttributes({ alignment: 'left' }).run()
                  } else {
                    editor.chain().setButtonAttributes({ alignment: 'center' }).run()
                  }
                }}
              >
                <FontAwesomeIcon icon={faAlignCenter} fixedWidth />
              </EditorCommandButton>
              <EditorCommandButton
                title="Align right"
                className="btn btn-sm mx-2"
                isActive={() => editor.isActive('button', { alignment: 'right' })}
                onClick={() => {
                  if (editor.getAttributes('image').alignment === 'right') {
                    editor.chain().setButtonAttributes({ alignment: 'left' }).run()
                  } else {
                    editor.chain().setButtonAttributes({ alignment: 'right' }).run()
                  }
                }}
              >
                <FontAwesomeIcon icon={faAlignRight} fixedWidth />
              </EditorCommandButton>
            </div>
          </div>
          <div
            style={{
              flexBasis: 0,
              flexGrow: 1,
              maxWidth: '100%',
            }}
          >
            <label className="small">Color</label>
            <div>
              <ColorButton
                id="button-background-color"
                title={
                  editor.isActive('button', { variant: 'outline' })
                    ? 'Border color'
                    : 'Background color'
                }
                className="btn btn-sm mx-2"
                value={editor.getAttributes('button').buttonColor}
                onChange={(evt) =>
                  editor
                    .chain()
                    .setButtonAttributes({
                      buttonColor:
                        !evt.target.value.includes('#') && evt.target.value.length === 6
                          ? `#${evt.target.value}`
                          : evt.target.value,
                    })
                    .run()
                }
              >
                <FontAwesomeIcon
                  icon={editor.isActive('button', { variant: 'outline' }) ? farSquare : faSquare}
                  color={editor.getAttributes('button').buttonColor}
                  fixedWidth={true}
                />
              </ColorButton>
              <ColorButton
                id="button-text-color"
                title="Text color"
                className="btn btn-sm mx-2"
                value={editor.getAttributes('button').textColor}
                onChange={(evt) =>
                  editor
                    .chain()
                    .setButtonAttributes({
                      textColor:
                        !evt.target.value.includes('#') && evt.target.value.length === 6
                          ? `#${evt.target.value}`
                          : evt.target.value,
                    })
                    .run()
                }
              >
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon icon={faText} fixedWidth={true} transform="shrink-4" />
                  <FontAwesomeIcon
                    icon={faHorizontalRule}
                    transform="shrink-4 down-8"
                    color={editor.getAttributes('button').textColor}
                    fixedWidth={true}
                  />
                </span>
              </ColorButton>
            </div>
          </div>
        </div>
        {linkOptions && (
          <SearchLink
            linkOptions={linkOptions}
            onSelect={(url) => {
              editor.chain().setButtonAttributes({ url }).run()
            }}
          />
        )}
      </div>
    </BubbleMenu>
  )
}

interface IColorButtonProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    Pick<EditorCommandButtonProps, 'title' | 'activeClassName' | 'isActive'> {}

const ColorButton = ({
  title,
  className,
  activeClassName,
  isActive,
  children,
  value,
  id = 'color',
  ...props
}: IColorButtonProps) => {
  const ref = useRef<HTMLLabelElement>(null)
  return (
    <label htmlFor={id} ref={ref}>
      <input
        id={id}
        {...props}
        type="color"
        aria-label="Color"
        style={{ maxHeight: 0, maxWidth: 0, opacity: 0, padding: 0, margin: -2 }}
      />
      <EditorCommandButton
        title={title}
        className={className}
        activeClassName={activeClassName}
        isActive={isActive}
        onClick={() => ref.current?.click()}
      >
        {children}
      </EditorCommandButton>
    </label>
  )
}
